import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { Box, Chip, Link, alpha } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { Channels } from "../../channels/types";
import { communicationsText } from "../program-timeline";

import type { ChannelName } from "../../channels/types";
import type { TimelineRow } from "../program-timeline";
import type { FC } from "react";

export const isChannel = (channel: string | null): channel is NonNullable<ChannelName> =>
  channel !== null && channel in Channels;

/** Each channel's name, in the order of the summary. */
export const CHANNEL_LABELS: Record<NonNullable<ChannelName>, string> = {
  work_email: "Work email",
  personal_email: "Personal email",
  slack: "Slack",
  teams: "Teams",
  whatsapp: "WhatsApp",
  twilio: "SMS",
};

/**
 * Each status's name, the colour of its filter chip's dot, and the colour of its chip. An archived
 * one's chip has only an outline, so that it does not look like a draft's.
 */
export const STATUS: Record<
  string,
  { label: string; dot: string; color?: "success" | "info" | "warning"; outlined?: boolean }
> = {
  active: { label: "Active", dot: "success.main", color: "success" },
  draft: { label: "Draft", dot: "neutral.300" },
  paused: { label: "Paused", dot: "warning.main", color: "warning" },
  finished: { label: "Finished", dot: "info.main", color: "info" },
  archived: { label: "Archived", dot: "neutral.600", outlined: true },
};

export const StatusChip: FC<{ status: string }> = ({ status }) => {
  const { label, color, outlined = false } = STATUS[status] ?? { label: status };
  return (
    <Chip
      size="small"
      label={label}
      variant={outlined ? "outlined" : "filled"}
      sx={{
        flexShrink: 0,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        bgcolor: (theme) => (color ? alpha(theme.palette[color].main, 0.12) : "action.hover"),
        color: color ? `${color}.dark` : "text.primary",
        ...(outlined && { bgcolor: "transparent", color: "text.secondary" }),
        "& .MuiChip-label": { px: 1.25 },
      }}
    />
  );
};

/** A small round dot in a colour. A Chip gives its icon a className, for the icon's margins. */
export const Dot: FC<{ color: string; size?: number; className?: string }> = ({
  color,
  size = 8,
  className,
}) => (
  <Box
    component="span"
    aria-hidden
    className={className}
    sx={{ width: size, height: size, borderRadius: "50%", bgcolor: color, flexShrink: 0 }}
  />
);

/** A group's number of Communications, in a pill, for the timeline and the list. */
export const CountPill: FC<{ count: number }> = ({ count }) => (
  <Box
    component="span"
    sx={{
      height: 24,
      px: 1.25,
      display: "inline-flex",
      alignItems: "center",
      border: 1,
      borderColor: "divider",
      borderRadius: "12px",
      bgcolor: "background.default",
      fontSize: 12,
      fontWeight: 600,
      color: "text.secondary",
      whiteSpace: "nowrap",
    }}
  >
    {communicationsText(count)}
  </Box>
);

/** The title of a Communication, as a link that opens it. */
export const TitleLink: FC<{ row: TimelineRow; covers?: boolean }> = ({ row, covers }) => (
  <Link
    component={RouterLink}
    to={`/moments/${row.id}`}
    underline="hover"
    color="inherit"
    sx={{
      fontWeight: 600,
      display: "block",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      ...(covers ? { "&::after": { content: '""', position: "absolute", inset: 0 } } : {}),
    }}
  >
    {row.title}
  </Link>
);

/** The folder of a Communication in a folder below, with a folder icon. */
export const FolderName: FC<{ name: string; fontSize: number }> = ({ name, fontSize }) => (
  <Box
    component="span"
    sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: "primary.dark" }}
  >
    <FolderOutlinedIcon aria-hidden sx={{ fontSize: fontSize + 2 }} />
    {name}
  </Box>
);
